import {
  UploadRuleCheckSchema,
  type UploadRule,
  type UploadRuleCheck,
  type UploadRuleColumn,
  type UploadRuleSeverity,
} from '@erp/rpc';

/**
 * The rule editor's state is plain text fields; this module turns it into a
 * contract-valid check (and back), so the editor component stays JSX.
 */

export type RuleCheckType = UploadRuleCheck['type'];

export interface RuleFormState {
  column: UploadRuleColumn;
  checkType: RuleCheckType;
  /** `equals` value, comma-separated `oneOf` values, or `maxLength` count. */
  value: string;
  min: string;
  max: string;
  severity: UploadRuleSeverity;
  /** The supplier layout the rule is limited to; null = every layout. */
  layoutId: string | null;
}

export type RuleFormError = 'value' | 'range' | 'length';

export const EMPTY_RULE_FORM: RuleFormState = {
  column: 'H',
  checkType: 'equals',
  value: '',
  min: '',
  max: '',
  severity: 'block',
  layoutId: null,
};

function optionalNumber(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function draftCheck(form: RuleFormState): unknown {
  switch (form.checkType) {
    case 'required':
      return { type: 'required' };
    case 'equals':
      return { type: 'equals', value: form.value.trim() };
    case 'oneOf':
      return {
        type: 'oneOf',
        values: form.value
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== ''),
      };
    case 'numberBetween':
      return {
        type: 'numberBetween',
        min: optionalNumber(form.min),
        max: optionalNumber(form.max),
      };
    case 'maxLength':
      return { type: 'maxLength', max: Number(form.value.trim() || NaN) };
  }
}

const ERROR_FOR_TYPE: Record<RuleCheckType, RuleFormError> = {
  required: 'value',
  equals: 'value',
  oneOf: 'value',
  numberBetween: 'range',
  maxLength: 'length',
};

export function checkFromForm(
  form: RuleFormState,
): { check: UploadRuleCheck } | { error: RuleFormError } {
  const draft = draftCheck(form);
  const parsed = UploadRuleCheckSchema.safeParse(draft);
  if (!parsed.success) return { error: ERROR_FOR_TYPE[form.checkType] };
  // An empty expected value would only ever match blanks, which every check
  // but `required` lets through — so it could never fail. Refuse it.
  if (parsed.data.type === 'equals' && parsed.data.value === '') {
    return { error: 'value' };
  }
  return { check: parsed.data };
}

function formFields(
  check: UploadRuleCheck,
): Pick<RuleFormState, 'value' | 'min' | 'max'> {
  switch (check.type) {
    case 'required':
      return { value: '', min: '', max: '' };
    case 'equals':
      return { value: check.value, min: '', max: '' };
    case 'oneOf':
      return { value: check.values.join(', '), min: '', max: '' };
    case 'numberBetween':
      return {
        value: '',
        min: check.min === null ? '' : String(check.min),
        max: check.max === null ? '' : String(check.max),
      };
    case 'maxLength':
      return { value: String(check.max), min: '', max: '' };
  }
}

export function formFromRule(rule: UploadRule): RuleFormState {
  return {
    column: rule.column,
    checkType: rule.check.type,
    severity: rule.severity,
    layoutId: rule.layoutId,
    ...formFields(rule.check),
  };
}
